// The API responses the server sends with a page's HTML (the backend's web/share_meta.php, which
// also fills in the page's text for what reads the HTML without running this app), in
// <script id="server-page-data">: { api, path, data } for the action and path the app asks for first
// at that address, and in `more` the responses of the lists under the body on that first screen
// ([{ api, query, data }]: ConferencesBlock.js, CoursesBlock.js). Read once at start-up; each is
// given out once (take...), so a later visit to the page asks the API. A component reads one while
// it is drawn (peek...) and takes it once shown, since React may draw a component twice.
const sent = (() => {
    try {
        const element = document.getElementById('server-page-data');
        return element ? JSON.parse(element.textContent) : null;
    } catch (e) {
        return null;
    }
})();

// The path the server answered for (its own address, a stored page's slug)
const servedPath = sent && typeof sent.path === 'string' ? sent.path : null;
let serverResponse = sent && servedPath !== null ? sent : null;
let blockResponses = sent && Array.isArray(sent.more) ? sent.more.filter((r) => r && typeof r.api === 'string') : [];

// Whether the server's responses are the ones for ?path=`path` (percent-encoded or not)
const isForPath = (path) => {
    let decoded = String(path || '');
    try {
        decoded = decodeURIComponent(decoded);
    } catch (e) {
        // as typed
    }
    return decoded.replace(/^\/+|\/+$/g, '') === servedPath;
};

// The response the API would give the app for `api` (e.g. "pages/getpagedata") with ?path=`path`,
// as { data }, when the server sent it with the page and it has not been taken; null otherwise
export const peekServerResponse = (api, path) => (
    serverResponse && serverResponse.api === api && isForPath(path) ? { data: serverResponse.data } : null
);

// peekServerResponse, and the response is given out: later calls ask the API
export const takeServerResponse = (api, path) => {
    const response = peekServerResponse(api, path);
    if (response) {
        serverResponse = null;
    }
    return response;
};

// The action the server sent its response for, when that response is for ?path=`path` (not taken
// yet); null otherwise. The server looks an address up in the order Page.js does, so the lookups
// before that action found nothing there.
export const serverResponseApi = (path) => (serverResponse && isForPath(path) ? serverResponse.api : null);

// Whether two query strings' parameters ({ page: 0 } and { page: '0' }) are the same request
const sameQuery = (a, b) => {
    const keysA = Object.keys(a || {});
    const keysB = Object.keys(b || {});
    return keysA.length === keysB.length
        && keysA.every((key) => keysB.includes(key) && String(a[key]) === String(b[key]));
};

const blockResponseAt = (api, query, path) => (
    isForPath(path) ? blockResponses.findIndex((response) => response.api === api && sameQuery(response.query, query)) : -1
);

// The response the API would give a block of the page at `path` for `api` (e.g.
// "pages/getconferences") with the query string's parameters `query` ({ page: 0 }), as { data },
// when the server sent it with the page and it has not been taken; null otherwise. Only for the
// page the server sent, so a list shown later or elsewhere is asked for fresh.
export const peekServerBlockResponse = (api, query, path) => {
    const at = blockResponseAt(api, query, path);
    return at < 0 ? null : { data: blockResponses[at].data };
};

// peekServerBlockResponse, and the response is given out
export const takeServerBlockResponse = (api, query, path) => {
    const at = blockResponseAt(api, query, path);
    if (at < 0) {
        return null;
    }
    const [response] = blockResponses.splice(at, 1);
    return { data: response.data };
};
